import { Box } from '@ltvco/refresh-lib/theme';
import { type ReactElement } from 'react';

import {
  ExploreByProductLayout,
  GraphicWrap,
  Intro,
  LinkBody,
  LinkDescription,
  LinkDivider,
  LinkTitle,
  ProductRowLink,
  RowChevron,
  SectionTitle,
} from './components';
import { LegalIntelligenceIconChip } from './LegalIntelligenceIconChip';

export { ExploreByProductCategoryStack } from './components';

export type ExploreProductLinkItem = {
  title: string;
  description: string;
  to: string;
  icon: ReactElement;
};

export type ExploreByProductCategoryProps = {
  sectionTitle: string;
  intro: string;
  navAriaLabel: string;
  links: ExploreProductLinkItem[];
  imageSrc: string;
  imageAlt: string;
  imageWidth?: number;
  imageHeight?: number;
};

function linkKey(link: ExploreProductLinkItem) {
  return `${link.to}::${link.title}`;
}

export function ExploreByProductCategory({
  sectionTitle,
  intro,
  navAriaLabel,
  links,
  imageSrc,
  imageAlt,
  imageWidth = 389,
  imageHeight = 245,
}: ExploreByProductCategoryProps) {
  return (
    <ExploreByProductLayout>
      <Box
        sx={(theme) => ({
          minWidth: 0,
          [theme.breakpoints.down('md')]: {
            paddingTop: theme.spacing(3),
            paddingBottom: theme.spacing(2.5),
          },
        })}
      >
        <Box
          sx={(theme) => ({
            [theme.breakpoints.down('md')]: {
              paddingLeft: theme.spacing(2.5),
              paddingRight: theme.spacing(2.5),
            },
          })}
        >
          <SectionTitle component="h3" variant="subtitle1">
            {sectionTitle}
          </SectionTitle>
          <Intro variant="body1" color="text.secondary">
            {intro}
          </Intro>
        </Box>

        <Box component="nav" aria-label={navAriaLabel}>
          {links.map((link, index) => (
            <Box key={linkKey(link)}>
              {index > 0 ? <LinkDivider /> : null}
              <ProductRowLink to={link.to}>
                <LegalIntelligenceIconChip aria-hidden $size="comfortable">
                  {link.icon}
                </LegalIntelligenceIconChip>
                <LinkBody>
                  <LinkTitle component="span" variant="subtitle2">
                    {link.title}
                  </LinkTitle>
                  <LinkDescription
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {link.description}
                  </LinkDescription>
                </LinkBody>
                <RowChevron aria-hidden />
              </ProductRowLink>
            </Box>
          ))}
        </Box>
      </Box>
      <GraphicWrap>
        <img
          src={imageSrc}
          alt={imageAlt}
          width={imageWidth}
          height={imageHeight}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </GraphicWrap>
    </ExploreByProductLayout>
  );
}
